import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Trophy,
  Scale,
  TrendingUp,
  ArrowRight,
  Loader2,
  Dog,
  MessageSquare,
  ShoppingBag,
  FileText,
  Crown,
  Medal,
  Award,
  ChevronLeft,
  ChevronRight,
  Zap,
  BarChart3,
  Users,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Rankings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('compare');
  const [category, setCategory] = useState('posts');
  const [loading, setLoading] = useState(false);
  const [comparisonPair, setComparisonPair] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [stats, setStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [comparisonsCount, setComparisonsCount] = useState(0);

  const categories = [
    { value: 'posts', label: 'Posts', icon: MessageSquare },
    { value: 'dogs', label: 'Dogs', icon: Dog },
    { value: 'marketplace', label: 'Marketplace', icon: ShoppingBag },
    { value: 'files', label: 'Files', icon: FileText }
  ];

  useEffect(() => {
    if (activeTab === 'compare' && user) {
      fetchComparisonPair();
    } else if (activeTab === 'leaderboard') {
      fetchRankings();
    } else if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab, category, user]);

  const fetchComparisonPair = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/comparisons/pair/${category}`);
      setComparisonPair(response.data);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Not enough items to compare');
        setComparisonPair(null);
      } else {
        toast.error('Failed to load comparison');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/rankings/${category}`);
      // Transform data to include computed fields
      const transformedRankings = response.data.map((item, index) => ({
        ...item,
        rank: index + 1,
        elo_rating: item.elo_score,
        win_rate: item.total_comparisons > 0 
          ? Math.round((item.wins / item.total_comparisons) * 100) 
          : 0
      }));
      setRankings(transformedRankings);
    } catch (error) {
      toast.error('Failed to load rankings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/comparisons/stats/${category}`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const handleComparison = async (winnerId) => {
    if (!comparisonPair || submitting) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/comparisons`, {
        item_a_id: comparisonPair.item_a.id,
        item_b_id: comparisonPair.item_b.id,
        winner_id: winnerId,
        category: category
      });
      
      setComparisonsCount(prev => prev + 1);
      toast.success('Preference recorded!');
      
      // Fetch next pair
      fetchComparisonPair();
    } catch (error) {
      toast.error('Failed to submit comparison');
    } finally {
      setSubmitting(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-warning" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-slate-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center font-bold text-muted-foreground">#{rank}</span>;
  };

  const CategoryIcon = categories.find(c => c.value === category)?.icon || MessageSquare;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-heading font-medium mb-2 flex items-center gap-3">
              <Scale className="w-10 h-10 text-primary" />
              Community Rankings
            </h1>
            <p className="text-muted-foreground">
              Compare items head-to-head to build community rankings
            </p>
          </div>

          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48" data-testid="category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  <div className="flex items-center gap-2">
                    <cat.icon className="w-4 h-4" />
                    {cat.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="compare" className="flex items-center gap-2">
              <Scale className="w-4 h-4" />
              Compare
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Leaderboard
            </TabsTrigger>
            <TabsTrigger value="stats" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Stats
            </TabsTrigger>
          </TabsList>

          {/* Compare Tab */}
          <TabsContent value="compare">
            {!user ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Scale className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">Sign in to Compare</h3>
                  <p className="text-muted-foreground mb-4">
                    Help rank content by comparing items head-to-head
                  </p>
                  <Button className="btn-primary" onClick={() => navigate('/login')}>
                    Sign In
                  </Button>
                </CardContent>
              </Card>
            ) : loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !comparisonPair ? (
              <Card className="text-center py-16">
                <CardContent>
                  <CategoryIcon className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">Not Enough Items</h3>
                  <p className="text-muted-foreground mb-4">
                    Need at least 2 items in this category to start comparing
                  </p>
                  <Button variant="outline" onClick={fetchComparisonPair}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Comparison Counter */}
                {comparisonsCount > 0 && (
                  <div className="flex justify-center">
                    <Badge variant="secondary" className="text-lg px-4 py-2">
                      <Zap className="w-4 h-4 mr-2 text-warning" />
                      {comparisonsCount} comparisons this session
                    </Badge>
                  </div>
                )}

                {/* Question */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-heading font-medium mb-2">
                    Which do you prefer?
                  </h2>
                  <p className="text-muted-foreground">
                    Click on the {category.slice(0, -1)} you think is better
                  </p>
                </div>

                {/* Comparison Cards */}
                <div className="grid md:grid-cols-2 gap-8 items-stretch">
                  {/* Item A */}
                  <Card 
                    className={`card-interactive cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
                    onClick={() => handleComparison(comparisonPair.item_a.id)}
                    data-testid="comparison-item-a"
                  >
                    <CardContent className="p-6">
                      {comparisonPair.item_a.image_url && (
                        <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-4">
                          <img 
                            src={comparisonPair.item_a.image_url} 
                            alt={comparisonPair.item_a.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <h3 className="text-xl font-heading font-medium mb-2">
                        {comparisonPair.item_a.title}
                      </h3>
                      {comparisonPair.item_a.content && (
                        <p className="text-muted-foreground text-sm line-clamp-3 mb-3">
                          {comparisonPair.item_a.content}
                        </p>
                      )}
                      {comparisonPair.item_a.price && (
                        <Badge variant="secondary">${comparisonPair.item_a.price}</Badge>
                      )}
                      {comparisonPair.item_a.author && (
                        <p className="text-xs text-muted-foreground mt-2">
                          by @{comparisonPair.item_a.author}
                        </p>
                      )}
                      <div className="mt-4 pt-4 border-t">
                        <Button className="w-full btn-primary" disabled={submitting}>
                          <ChevronLeft className="w-4 h-4 mr-2" />
                          Choose This
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* VS Divider */}
                  <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="w-16 h-16 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-heading text-xl font-bold shadow-lg">
                      VS
                    </div>
                  </div>

                  {/* Item B */}
                  <Card 
                    className={`card-interactive cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${submitting ? 'opacity-50 pointer-events-none' : ''}`}
                    onClick={() => handleComparison(comparisonPair.item_b.id)}
                    data-testid="comparison-item-b"
                  >
                    <CardContent className="p-6">
                      {comparisonPair.item_b.image_url && (
                        <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-4">
                          <img 
                            src={comparisonPair.item_b.image_url} 
                            alt={comparisonPair.item_b.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <h3 className="text-xl font-heading font-medium mb-2">
                        {comparisonPair.item_b.title}
                      </h3>
                      {comparisonPair.item_b.content && (
                        <p className="text-muted-foreground text-sm line-clamp-3 mb-3">
                          {comparisonPair.item_b.content}
                        </p>
                      )}
                      {comparisonPair.item_b.price && (
                        <Badge variant="secondary">${comparisonPair.item_b.price}</Badge>
                      )}
                      {comparisonPair.item_b.author && (
                        <p className="text-xs text-muted-foreground mt-2">
                          by @{comparisonPair.item_b.author}
                        </p>
                      )}
                      <div className="mt-4 pt-4 border-t">
                        <Button className="w-full btn-primary" disabled={submitting}>
                          Choose This
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Skip Button */}
                <div className="flex justify-center">
                  <Button 
                    variant="ghost" 
                    onClick={fetchComparisonPair}
                    disabled={submitting}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Skip / Get New Pair
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : rankings.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Trophy className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No Rankings Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Be the first to compare items and build the leaderboard!
                  </p>
                  <Button className="btn-primary" onClick={() => setActiveTab('compare')}>
                    Start Comparing
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Top 3 Podium */}
                {rankings.length >= 3 && (
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {/* 2nd Place */}
                    <Card className="text-center pt-8 mt-8">
                      <CardContent>
                        <Medal className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                        <h3 className="font-heading font-medium truncate">{rankings[1]?.title}</h3>
                        <p className="text-2xl font-bold text-primary">{Math.round(rankings[1]?.elo_rating)}</p>
                        <p className="text-sm text-muted-foreground">{rankings[1]?.wins}W - {rankings[1]?.losses}L</p>
                      </CardContent>
                    </Card>
                    
                    {/* 1st Place */}
                    <Card className="text-center bg-gradient-to-b from-warning/20 to-transparent border-warning/50">
                      <CardContent className="pt-6">
                        <Crown className="w-16 h-16 mx-auto text-warning mb-2" />
                        <h3 className="font-heading text-lg font-medium truncate">{rankings[0]?.title}</h3>
                        <p className="text-3xl font-bold text-primary">{Math.round(rankings[0]?.elo_rating)}</p>
                        <p className="text-sm text-muted-foreground">{rankings[0]?.wins}W - {rankings[0]?.losses}L</p>
                      </CardContent>
                    </Card>
                    
                    {/* 3rd Place */}
                    <Card className="text-center pt-12 mt-12">
                      <CardContent>
                        <Award className="w-10 h-10 mx-auto text-amber-600 mb-2" />
                        <h3 className="font-heading font-medium truncate">{rankings[2]?.title}</h3>
                        <p className="text-2xl font-bold text-primary">{Math.round(rankings[2]?.elo_rating)}</p>
                        <p className="text-sm text-muted-foreground">{rankings[2]?.wins}W - {rankings[2]?.losses}L</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Full Rankings List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Full Rankings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {rankings.map((item) => (
                        <div 
                          key={item.id}
                          className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                          data-testid={`ranking-item-${item.id}`}
                        >
                          <div className="w-10 flex justify-center">
                            {getRankIcon(item.rank)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium truncate">{item.title}</h4>
                            <p className="text-sm text-muted-foreground">
                              {item.wins}W - {item.losses}L ({item.win_rate}% win rate)
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-primary">{Math.round(item.elo_rating)}</p>
                            <p className="text-xs text-muted-foreground">Elo Rating</p>
                          </div>
                          <div className="w-24">
                            <Progress value={item.win_rate} className="h-2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !stats ? (
              <Card className="text-center py-16">
                <CardContent>
                  <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No Stats Yet</h3>
                  <p className="text-muted-foreground">
                    Start comparing to generate statistics
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Scale className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-3xl font-heading font-bold">{stats.total_comparisons}</p>
                        <p className="text-sm text-muted-foreground">Total Comparisons</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                        <CategoryIcon className="w-6 h-6 text-secondary" />
                      </div>
                      <div>
                        <p className="text-3xl font-heading font-bold">{stats.total_ranked_items}</p>
                        <p className="text-sm text-muted-foreground">Ranked Items</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center">
                        <Users className="w-6 h-6 text-accent-foreground" />
                      </div>
                      <div>
                        <p className="text-3xl font-heading font-bold">{stats.unique_contributors}</p>
                        <p className="text-sm text-muted-foreground">Contributors</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-warning" />
                      </div>
                      <div>
                        <p className="text-lg font-heading font-bold truncate max-w-[150px]">
                          {stats.top_items?.[0]?.title || 'N/A'}
                        </p>
                        <p className="text-sm text-muted-foreground">Current #1</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Top Items List */}
                {stats.top_items?.length > 0 && (
                  <Card className="md:col-span-2 lg:col-span-4">
                    <CardHeader>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <Crown className="w-5 h-5 text-warning" />
                        Top Ranked {category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stats.top_items.map((item, idx) => (
                          <div key={item.id || item.title} className="flex items-center gap-4">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-bold">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{item.title}</p>
                            </div>
                            <Badge variant="secondary">
                              {Math.round(item.elo_rating)} Elo
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Rankings;
